import { MdInfo } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import React, { useContext, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type { ScenariosResponse } from "~/data/scenario/scenario.service";

import logo from "~/ui/shared/assets/logo-extended.png";
import { getScenarios } from "~/data/scenario/scenario.service";

import { Button } from "../shared/button";
import { Dialog } from "../shared/dialog";
import { Spinner } from "../shared/spinner";
import { Tooltip } from "../shared/tooltip";
import { InputSearch } from "../shared/input";
import { StarsIcon } from "../shared/icons/stars";
import { HtmlContent } from "../shared/html-content";
import { useUser } from "../shared/queries/user-query";
import { scenariosQuery } from "./queries/scenarios-query";
import { useDebounced } from "../shared/utils/use-debounced";
import { CallContext } from "../shared/contexts/call-context";
import { useCreateUserTestMutation } from "./queries/create-user-test-mutation";
import { useIntesectionObserver } from "../shared/utils/use-intersection-observer";

interface CategoryScenariosProps extends React.ComponentProps<"div"> {
  category?: {
    id: string;
    name: string;
  };
}

export function ScenariosByCategory({
  category,
  ...props
}: CategoryScenariosProps) {
  const [keyword, setKeyword] = useState("");
  const [year, setYear] = useState("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const call = useContext(CallContext);

  const variables = {
    category_id: category?.id,
    query: useDebounced(keyword, 300),
    inep_date: useDebounced(year, 300),
  };
  const { data, isLoading, isPlaceholderData } = useQuery({
    ...scenariosQuery(variables),
    placeholderData: keepPreviousData,
  });
  const createUserTestMutation = useCreateUserTestMutation();
  const loadMoreSceneariosMutation = useMutation({
    mutationFn: (page: number) => getScenarios({ page, ...variables }),
    onSuccess: (data) =>
      queryClient.setQueryData<ScenariosResponse>(
        scenariosQuery(variables).queryKey,
        (state) => state && { ...data, data: [...state.data, ...data.data] }
      ),
  });
  const ref = useIntesectionObserver<HTMLLIElement>(() => {
    if (!data?.next || loadMoreSceneariosMutation.isPending) return;
    loadMoreSceneariosMutation.mutate(data.next);
  });

  const user = useUser();
  const mate = call
    ? call.user1.id === user.id
      ? call.user2!
      : call.user1
    : null;
  const canCreateTest =
    (user.has_active_subscription ||
      user.is_trial ||
      user.admin ||
      user.actor ||
      user.partner) &&
    (mate ? !mate.actor : true);

  return (
    <div {...props}>
      <div className="flex items-center gap-3 py-2 px-[10px]">
        <h2 className="inline-flex items-center font-alternative text-3xl font-semibold">
          {category?.name ?? "Todos"}{" "}
          {!!data && !isPlaceholderData && (
            <span className="text-base ml-[10px]">({data.total} cenários)</span>
          )}
        </h2>

        <div className="flex px-3 ml-auto">
          {!!category && (
            <>
              <button
                type="button"
                className="text-xl leading-5 font-bold h-[58px] inline-flex items-center px-5 rounded-3xl bg-secondary-2 text-white disabled:opacity-70"
                disabled={createUserTestMutation.isPending}
                onClick={() => {
                  createUserTestMutation.mutate(
                    {
                      test_type: "ai",
                      category_id: category.id,
                      is_random: true,
                    },
                    { onSuccess: (data) => navigate(`/praticas/${data.id}`) }
                  );
                }}
              >
                <span className="px-1">IA</span> <StarsIcon />
              </button>
              <Tooltip
                content={
                  <span>
                    Deixe a IA escolher o melhor
                    <br /> checklist para você de acordo <br />
                    com seus últimos resultados.
                  </span>
                }
              >
                <MdInfo color="#5B5B5B" size={24} />
              </Tooltip>
            </>
          )}
        </div>
        <InputSearch
          placeholder="Busque por palavra chave"
          className="w-64 max-w-full"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <InputSearch
          placeholder="Busque por exame/ano"
          className="w-64 max-w-full"
          value={year}
          onChange={(e) => setYear(e.target.value)}
        />
      </div>

      <ul
        data-pending={isPlaceholderData}
        className="grid gap-10 max-h-[70vh] overflow-y-auto px-[10px] py-[10px] pb-5 data-[pending=true]:opacity-70"
      >
        {!data &&
          isLoading &&
          Array.from({ length: 10 }, (_, i) => (
            <li
              key={`placeholder:${i}`}
              className="h-[72px] rounded-[10px] bg-[#F4F4F4] animate-pulse"
            />
          ))}

        {data?.rows.map((it, i, self) => (
          <li
            key={it.id}
            className="flex items-center gap-[10px] rounded-[10px] px-6 py-4 border-l-[6px] border-l-primary-1 bg-surface"
            style={{ boxShadow: "0px 10px 20px 0px rgba(0, 0, 0, 0.08)" }}
            ref={self.length - 1 === i ? ref : undefined}
          >
            <h3 className="flex-1 text-lg font-alternative font-medium leading-6">
              {it.title}{" "}
              {it.resume && (
                <Dialog>
                  <Dialog.Trigger asChild>
                    <button
                      type="button"
                      aria-label="Ver resumo"
                      title="Ver resumo"
                      className="align-middle"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15.075 6.875H20.025L15.075 2.0625V6.875ZM8.775 0.75H15.975L21.375 6V16.5C21.375 16.9641 21.1854 17.4092 20.8478 17.7374C20.5102 18.0656 20.0524 18.25 19.575 18.25H8.775C8.29761 18.25 7.83977 18.0656 7.50221 17.7374C7.16464 17.4092 6.975 16.9641 6.975 16.5V2.5C6.975 2.03587 7.16464 1.59075 7.50221 1.26256C7.83977 0.934374 8.29761 0.75 8.775 0.75M5.175 4.25V20H19.575V21.75H5.175C4.69761 21.75 4.23977 21.5656 3.90221 21.2374C3.56464 20.9092 3.375 20.4641 3.375 20V4.25H5.175Z"
                          fill="#5B5B5B"
                        />
                      </svg>
                    </button>
                  </Dialog.Trigger>
                  <Dialog.Content className="max-w-xl">
                    <div>
                      <header className=" border-b-[5px] border-b-secondary-1 pb-5 mb-6">
                        <img
                          className="block mx-auto h-[95px]"
                          height={95}
                          src={logo}
                        />
                      </header>
                      <HtmlContent content={it.resume} />
                    </div>
                  </Dialog.Content>
                </Dialog>
              )}
            </h3>

            {canCreateTest && (
              <Button
                variant="primary-1"
                className="w-60 h-10 ml-auto"
                disabled={createUserTestMutation.isPending}
                onClick={() =>
                  createUserTestMutation.mutate(
                    {
                      test_id: it.id,
                      test_type: "patient",
                      chat_room_id: call?.id, // links call id to user test
                    },
                    { onSuccess: (data) => navigate(`/praticas/${data.id}`) }
                  )
                }
                loading={
                  createUserTestMutation.isPending &&
                  createUserTestMutation.variables.test_type === "patient" &&
                  createUserTestMutation.variables.test_id === it.id
                }
              >
                Praticar com colega
              </Button>
            )}

            {/* Doesnt' render AI button if has a human call */}
            {!user.actor && canCreateTest && !call && (
              <Button
                variant="secondary-1"
                className="w-60 h-10"
                disabled={createUserTestMutation.isPending}
                onClick={() =>
                  createUserTestMutation.mutate(
                    { test_id: it.id, test_type: "ai" },
                    {
                      onSuccess: (data) => navigate(`/praticas/${data.id}`),
                    }
                  )
                }
                loading={
                  createUserTestMutation.isPending &&
                  createUserTestMutation.variables.test_type === "ai" &&
                  createUserTestMutation.variables.test_id === it.id
                }
              >
                Estudar com IA
              </Button>
            )}
          </li>
        ))}

        {loadMoreSceneariosMutation.isPending && (
          <li className="w-max mx-auto py-4">
            <Spinner size={32} />
          </li>
        )}
      </ul>
    </div>
  );
}
